/**
 * Decides how each question is answered:
 *   1. A recognised common question → exact answer from the snapshot
 *      (source "data"), no model call.
 *   2. Anything else → the backend LLM with a compact grounded context
 *      (source "ai"); nav links come from validated markers or, failing
 *      that, from keywords in the question.
 *   3. The model call fails → a data summary instead of a dead end
 *      (source "fallback"), so a live demo never shows a bare error.
 */
import { buildAssistantContext } from './contextBuilder';
import { answerIntent, detectIntent, detectTopic } from './intents';
import { extractNavActions, suggestNavActions, type NavAction } from './navTargets';
import type { PortfolioSnapshot } from './types';

export type ReplySource = 'data' | 'ai' | 'fallback';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface EngineReply {
  content: string;
  actions: NavAction[];
  source: ReplySource;
}

export type ChatFn = (messages: ChatTurn[], context: string) => Promise<string>;

export interface ReplyRequest {
  question: string;
  history: ChatTurn[];
  snapshot: PortfolioSnapshot;
  pageLabel?: string;
  chat: ChatFn;
}

const MAX_HISTORY_TURNS = 6;

/** Tidies common small-model habits so replies read cleanly. */
export function cleanModelReply(reply: string): string {
  return reply
    .replace(/^\s*(as an ai( language model)?|as re:loam assistant)[^.,]*[.,]\s*/i, '')
    .replace(/\bNGN\s?(\d)/g, '₦$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export async function generateReply(req: ReplyRequest): Promise<EngineReply> {
  const intent = detectIntent(req.question);
  if (intent) {
    return { ...answerIntent(intent, req.snapshot), source: 'data' };
  }

  // Advisory question about a known topic ("who owes the most and what should
  // I do?"): hand the model the exact answer for that topic up front.
  const topic = detectTopic(req.question);
  const focus = topic ? answerIntent(topic, req.snapshot).content : undefined;
  const context = buildAssistantContext(req.snapshot, { pageLabel: req.pageLabel, focus });
  const messages: ChatTurn[] = [...req.history.slice(-MAX_HISTORY_TURNS), { role: 'user', content: req.question }];
  try {
    const raw = await req.chat(messages, context);
    const { text, actions } = extractNavActions(cleanModelReply(raw));
    if (!text) throw new Error('Empty reply');
    return { content: text, actions: actions.length ? actions : suggestNavActions(req.question), source: 'ai' };
  } catch (error) {
    console.warn('Assistant model call failed, answering from data instead:', error);
    const summary = answerIntent('summary', req.snapshot);
    return {
      content: `I couldn't reach the AI model just now, so here's what I can tell you from your data.\n\n${summary.content}`,
      actions: summary.actions,
      source: 'fallback',
    };
  }
}
